import { Add, CloudUpload, Download, FilterList, CalendarToday,   Refresh,Close , Search,ExpandMore, GetApp } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  Grid,
  Tabs,
  Tab,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Fade,
  Collapse,
  Stack,
  Badge,
  useTheme,
  alpha,
  styled,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Skeleton,
  Alert,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Tooltip
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import Public from '@mui/icons-material/Public';
import LibraryBooks from '@mui/icons-material/LibraryBooks';
import CurrencyRupee from '@mui/icons-material/CurrencyRupee';
import FlexBetween from "components/flexbox/FlexBetween";
import FlexBox from "components/flexbox/FlexBox";
import SearchInput from "components/input-fields/SearchInput";
import { H6 } from "components/Typography";
import DataTable from "page-sections/data-table/dataTableV2/DataTable";
import LeadCard from "page-sections/dashboards/CRM/LeadCard";
import LongCard from "page-sections/dashboards/CRM/LongCard";
import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import AddDistrictModal from "page-sections/data-table/dataTableV2/AddDistrictModal";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { usePageTitle } from "contexts/PageTitleContext";
import BulkProductList, {
  consumeSavedBulkOrderListState,
  consumeSavedBulkOrderStatusTab,
} from "./bulkProductList";
import {
  setBulkOrderListSnapshot,
  takeBulkOrderListSnapshotIfReturning,
  acknowledgeBulkOrderListRestore,
} from "./bulkOrderListCache";
import AdvancedExportModal from "./AdvancedExportModal";
import AppPaginationBar from "components/AppPaginationBar";
import buttonConfig, { isOrderStatusTabDisabled, mergeOrderStatusLists } from "config/ConfigAccessButton";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";


// Export utility functions
const exportToCSV = (data, filename = 'bulk_products_export.csv') => {
  if (!data || data.length === 0) {
    alert('No data to export');
    return;
  }

  // Define headers for CSV
  const headers = [
    'Order ID',
    'Product Name',
    'SKU',
    'Customer Name',
    'Mobile Number',
    'Email',
    'User Category',
    'Source',
    'Quantity',
    'UOM',
    'Price',
    'Total Amount',
    'Order Status',
    'Payment Status',
    'GST Number',
    'Country',
    'State',
    'District',
    'Tehsil',
    'Village',
    'Pincode',
    'Order Date',
    'Created At'
  ];

  // Convert data to CSV format
  const csvContent = [
    headers.join(','), // Header row
    ...data.map(product => [
      `"${product.order?.order_id || ''}"`,
      `"${product.product?.name || ''}"`,
      `"${product.product?.sku || ''}"`,
      `"${product.end_user?.first_name || ''}"`,
      `"${product.end_user?.mobile_number || ''}"`,
      `"${product.end_user?.email || ''}"`,
      `"${product.category || ''}"`,
      `"${product.source || ''}"`,
      `"${product.quantity || ''}"`,
      `"${product.uom || ''}"`,
      `"${product.discounted_price || ''}"`,
      `"${product.order?.subtotal || ''}"`,
      `"${product.order?.status || ''}"`,
      `"${product.payment_status || ''}"`,
      `"${product.gst_no || ''}"`,
      `"${product.end_user?.country || ''}"`,
      `"${product.end_user?.state || ''}"`,
      `"${product.end_user?.district || ''}"`,
      `"${product.end_user?.tehsil || ''}"`,
      `"${product.end_user?.village || ''}"`,
      `"${product.end_user?.pincode || ''}"`,
      `"${product.order?.order_date ? new Date(product.order.order_date).toLocaleDateString() : ''}"`,
      `"${product.order?.created_at ? new Date(product.order.created_at).toLocaleDateString() : ''}"`
    ].join(','))
  ].join('\n');

  // *Create and download file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const exportToExcel = async (data, filename = 'bulk_products_export.xlsx') => {
  if (!data || data.length === 0) {
    alert('No data to export');
    return;
  }

  try {
    // Dynamic import for xlsx library
    const XLSX = await import('xlsx');
    
    // Prepare data for Excel
    const excelData = data.map(product => ({
      'Order ID': product.order.order_id || '',
      'Product Name': product.product.name || '',
      'SKU': product.product.sku || '',
      'Customer Name': product.end_user.first_name || '',
      'Mobile Number': product.end_user.mobile_number || '',
      'Email': product.end_user?.email || '',
      'User Category': product.category || '',
      'Source': product.source || '',
      'Quantity': product.quantity || 0,
      'UOM': product.uom || '',
      'Price': product.discounted_price || 0,
      'Total Amount': product.order.subtotal || 0,
      'Order Status': product.order.status || '',
      'Payment Status': product.payment_status || '',
      'GST Number': product.gst_no || '',
      'Country': product.end_user?.country || '',
      'State': product.end_user?.state || '',
      'District': product.end_user?.district || '',
      'Tehsil': product.end_user?.tehsil || '',
      'Village': product.end_user?.village || '',
      'Pincode': product.end_user?.pincode || '',
      'Order Date': product.order.order_date ? new Date(product.order.order_date).toLocaleDateString('en-IN') : '',
      'Created At': product.order.created_at ? new Date(product.order.created_at).toLocaleDateString('en-IN') : ''
    }));

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Get the range of the worksheet
    const range = XLSX.utils.decode_range(worksheet['!ref']);

    // Style definitions
    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "366092" } },
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } }
      }
    };

    const dataStyle = {
      alignment: { horizontal: "left", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "D3D3D3" } },
        bottom: { style: "thin", color: { rgb: "D3D3D3" } },
        left: { style: "thin", color: { rgb: "D3D3D3" } },
        right: { style: "thin", color: { rgb: "D3D3D3" } }
      }
    };

    const numberStyle = {
      ...dataStyle,
      alignment: { horizontal: "right", vertical: "center" },
      numFmt: "#,##0.00"
    };

    const dateStyle = {
      ...dataStyle,
      alignment: { horizontal: "center", vertical: "center" },
      numFmt: "dd/mm/yyyy"
    };

    // Apply styles to cells
    for (let row = range.s.r; row <= range.e.r; row++) {
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        const cell = worksheet[cellAddress];
        
        if (!cell) continue;

        // Header row styling
        if (row === 0) {
          cell.s = headerStyle;
        } else {
          // Get column header to determine data type
          const headerCell = worksheet[XLSX.utils.encode_cell({ r: 0, c: col })];
          const headerValue = headerCell ? headerCell.v : '';
          
          // Apply specific styles based on column type
          if (headerValue.includes('Price') || headerValue.includes('Amount') || headerValue.includes('Quantity')) {
            cell.s = numberStyle;
          } else if (headerValue.includes('Date')) {
            cell.s = dateStyle;
          } else {
            cell.s = dataStyle;
          }

          // Add alternating row colors
          if (row % 2 === 0) {
            cell.s = {
              ...cell.s,
              fill: { fgColor: { rgb: "F8F9FA" } }
            };
          }
        }
      }
    }

    // Auto-size columns with better width calculation
    const colWidths = Object.keys(excelData[0] || {}).map((key, index) => {
      let maxWidth = key.length;
      
      // Calculate max width for each column
      excelData.forEach(row => {
        const cellValue = String(row[key] || '');
        maxWidth = Math.max(maxWidth, cellValue.length);
      });
      
      // Set minimum and maximum widths
      const width = Math.min(Math.max(maxWidth + 2, 12), 50);
      return { wch: width };
    });
    
    worksheet['!cols'] = colWidths;

    // Set row height for header
    worksheet['!rows'] = [{ hpt: 25 }]; // Header row height

    // Add worksheet to workbook with a descriptive name
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Product Orders Export');

    // Add metadata
    workbook.Props = {
      Title: "Bulk Products Export",
      Subject: "Product Orders Data",
      Author: "System Export",
      CreatedDate: new Date()
    };

    // Save file with timestamp
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const finalFilename = filename.replace('.xlsx', `_${timestamp}.xlsx`);
    
    XLSX.writeFile(workbook, finalFilename);
    
    // Success message
    console.log(`Excel file exported successfully: ${finalFilename}`);
    
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    // alert('Error exporting to Excel. Please try again or use CSV export instead.');
  }
};

// Styled Components
const FilterContainer = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  overflow: 'visible',
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  borderRadius: theme.spacing(2),
  boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
}));

const FilterHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  background: alpha(theme.palette.primary.main, 0.02),
  borderRadius: `${theme.spacing(2)} ${theme.spacing(2)} 0 0`,
}));

const FilterContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  position: 'relative',
  zIndex: 1000,
}));

const ButtonWrapper = styled(FlexBox)(({
  theme
}) => ({
  [theme.breakpoints.down(500)]: {
    marginTop: 10,
    width: "100%",
    flexDirection: "column-reverse",
    "& > .MuiBox-root": {
      width: "100%",
      margin: "10px 0",
      alignItems: "center",
      flexDirection: "column"
    },
    "& .MuiButton-root": {
      minWidth: "100%"
    }
  }
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  minHeight: 42,
  width: '100%',
  '& .MuiTabs-indicator': {
    display: 'none',
  },
  '& .MuiTabs-flexContainer': {
    gap: 6,
    padding: 6,
  },
  '& .MuiTabs-scroller': {
    [theme.breakpoints.up('md')]: {
      '& .MuiTabs-flexContainer': {
        justifyContent: 'space-between',
      },
    },
  },
  boxShadow: theme.palette.mode === 'dark' 
    ? '0 2px 8px rgba(0,0,0,0.3)' 
    : '0 2px 8px rgba(0,0,0,0.1)',
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.8125rem',
  minHeight: 34,
  borderRadius: theme.shape.borderRadius,
  margin: 0,
  padding: '6px 12px',
  color: theme.palette.text.secondary,
  transition: 'all 0.2s ease-in-out',
  position: 'relative',
  
  [theme.breakpoints.up('md')]: {
    flex: 1,
    minWidth: 0,
  },
  
  [theme.breakpoints.down('md')]: {
    minWidth: 112,
    padding: '6px 10px',
    fontSize: '0.8rem',
  },
  
  [theme.breakpoints.down('sm')]: {
    minWidth: 96,
    padding: '6px 8px',
    fontSize: '0.75rem',
  },

  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.primary.main,
  },
  '&.Mui-disabled': {
    opacity: 0.45,
    cursor: 'not-allowed',
    color: theme.palette.text.disabled,
    pointerEvents: 'auto',
  },
  '&.Mui-selected': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
}));

const OrderStatusCountBadge = styled('span', {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ theme, active }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 32,
  height: 26,
  padding: '4px 10px',
  borderRadius: 999,
  fontSize: '0.875rem',
  fontWeight: 800,
  lineHeight: 1,
  flexShrink: 0,
  fontVariantNumeric: 'tabular-nums',
  letterSpacing: '0.02em',
  ...(active
    ? {
        backgroundColor: theme.palette.common.white,
        color: theme.palette.primary.main,
        border: `1.5px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.18)',
      }
    : {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        border: `1.5px solid ${alpha(theme.palette.primary.dark, 0.35)}`,
        boxShadow: '0 2px 6px rgba(25, 118, 210, 0.28)',
      }),
}));

const getStatusColor = (theme, statusCategory) => {
  const colors = {
    all: theme.palette.text.primary,
    pending: theme.palette.warning.main,
    confirmed: theme.palette.info.main,
    processing: theme.palette.info.main,
    shipped: theme.palette.secondary.main,
    delivered: theme.palette.success.main,
    cancelled: theme.palette.error.main,
    returned: theme.palette.error.dark,
  };
  const key = typeof statusCategory === 'string' ? statusCategory.toLowerCase() : statusCategory;
  return colors[key] || theme.palette.grey[500];
};

const getStatusIcon = (category) => {
  const key = (category || '').toLowerCase();
  switch (key) {
    case 'pending': return '⏳';
    case 'confirmed': return '✅';
    case 'processing': return '⚙️';
    case 'shipped': return '🚚';
    case 'delivered': return '📦';
    case 'cancelled': return '❌';
    case 'returned': return '↩️';
    case 'all': return '📊';
    default: return '🏷️';
  }
};

const TabContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  position: 'sticky',
  top: 0,
  zIndex: 10,
  backgroundColor: theme.palette.background.default,
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
}));

const UploadArea = styled(Box)(({ theme, isDragActive }) => ({
  border: `2px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius * 2,
  padding: theme.spacing(4),
  textAlign: 'center',
  backgroundColor: isDragActive ? alpha(theme.palette.primary.main, 0.1) : theme.palette.background.paper,
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
  }
}));

const DateFilterContainer = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.palette.mode === 'dark' 
    ? '0 2px 8px rgba(0,0,0,0.3)' 
    : '0 2px 8px rgba(0,0,0,0.1)',
}));

const StyledDatePicker = styled(DatePicker)(({ theme }) => ({
  '& .react-datepicker-wrapper': {
    width: '100%',
  },
  '& .react-datepicker__input-container input': {
    width: '100%',
    padding: '12px 16px',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    fontSize: '0.875rem',
    '&:focus': {
      outline: 'none',
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
    },
  },
}));

const SearchContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  '& .MuiTextField-root': {
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.spacing(2),
      backgroundColor: theme.palette.background.paper,
      transition: 'all 0.3s ease',
      '&:hover': {
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      },
      '&.Mui-focused': {
        boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.2)}`,
      },
    },
  },
}));

const DatePickerField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(1.5),
    backgroundColor: theme.palette.background.paper,
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    },
    '&.Mui-focused': {
      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
    },
  },
   '& .MuiInputBase-input': {
    cursor: 'pointer',
    colorScheme: theme.palette.mode === 'dark' ? 'dark' : 'light',
  },
  '& input[type="date"]': {
    colorScheme: theme.palette.mode === 'dark' ? 'dark' : 'light',
  },
  '& input[type="date"]::-webkit-calendar-picker-indicator': {
    filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'invert(0)',
    cursor: 'pointer',
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(1.5),
  textTransform: 'none',
  fontWeight: 600,
  padding: theme.spacing(1, 2.5),
  boxShadow: 'none',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
}));

const StatusChip = styled(Card)(({ theme, statuscolor }) => ({
  fontWeight: 600,
  borderRadius: theme.spacing(1),
  padding: theme.spacing(0.5, 1.5),
  display: 'inline-flex',
  alignItems: 'center',
  backgroundColor: statuscolor ? alpha(statuscolor, 0.1) : alpha(theme.palette.grey[500], 0.1),
  color: statuscolor || theme.palette.grey[700],
  border: `1px solid ${statuscolor ? alpha(statuscolor, 0.2) : alpha(theme.palette.grey[500], 0.2)}`,
  fontSize: '0.75rem',
}));

const BulkProducts = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const { setPageTitle } = usePageTitle();
  const pageSizeOptions = [10, 25, 50, 100];
  const [currentPage, setCurrentPage] = useState(1);

  // Toast state
  const [toast, setToast] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Check if user is Company type
  const isCompanyUser = apiService.isCompanyUser();
  const companyId = apiService.getCompanyId();
  const [districts, setDistricts] = useState([]);
  const [summaryTotals, setSummaryTotals] = useState({ total_orders: 0, total_amount: 0 });
  const [searchValue, setSearchValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isBlinking, setIsBlinking] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  // Initialize dates as strings in YYYY-MM-DD format for HTML date inputs
  const getFirstDayOfMonth = () => {
    const date = new Date();
    date.setDate(1);
    return date.toISOString().split('T')[0];
  };
  const getToday = () => {
    return new Date().toISOString().split('T')[0];
  };
  const [startDate, setStartDate] = useState(getFirstDayOfMonth());
  const [endDate, setEndDate] = useState(getToday());
  const [showDateFilter, setShowDateFilter] = useState(false);
   // Pagination state coming from backend
   const [pagination, setPagination] = useState({
    current_page: 1,
    page_size: 10,
    total_items: 0,
    total_pages: 1,
    has_next: false,
    has_previous: false
  });
  const [products, setProducts] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [isResetClicked, setIsResetClicked] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(isCompanyUser ? companyId : 'all');

  // Selection states
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [isAllSelected, setIsAllSelected] = useState(false);

  // Modal states
  const [bulkUploadModal, setBulkUploadModal] = useState(false);
  const [advancedExportModal, setAdvancedExportModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadSuccessMessage, setUploadSuccessMessage] = useState("");
  const [uploadResults, setUploadResults] = useState(null);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const fileInputRef = useRef(null);

  // ============ PERMISSION FUNCTIONS ============
  // Get current tab name based on status filter
  const getCurrentTabName = () => {
    const tabMapping = {
      'Placed': 'Placed Button',
      'Confirmed': 'Confirmed Button',
      'Processing': 'Processing Button',
      'Dispatched': 'Dispatch Button',
      'Dispatch': 'Dispatch Button',
      'Delivered': 'Delivered Button',
      'Cancelled': 'Cancelled Button',
      'Returned': 'Returned Button'
    };
    return tabMapping[statusFilter] || 'Placed Button';
  };
  
  // Get button permissions based on current tab
  const getCurrentButtonPermissions = () => {
    const currentTab = getCurrentTabName();
    const moduleConfig = buttonConfig["Bulk Order"];
    return moduleConfig && moduleConfig[currentTab] ? moduleConfig[currentTab] : {};
  };
  
  // Check if a specific button should be enabled
  const shouldEnableButton = (buttonKey) => {
    const permissions = getCurrentButtonPermissions();
    return permissions[buttonKey] === true;
  };
  // ============ END PERMISSION FUNCTIONS ============

  const handleToastClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setToast({ ...toast, open: false });
  };

  const showToast = (message, severity = 'success') => {
    setToast({
      open: true,
      message,
      severity
    });
  };

  const extractErrorMessage = async (response) => {
    try {
      if (response instanceof Blob) {
        const text = await response.text();
        try {
          const data = JSON.parse(text);
          return data.message || data.error || 'Upload failed';
        } catch {
          return text || 'Upload failed';
        }
      }
      
      if (typeof response === 'object') {
        return response.message || response.error || response.error_message || 'Upload failed';
      }
      
      return 'Upload failed';
    } catch (error) {
      return 'An unexpected error occurred';
    }
  };

  useEffect(() => {
    setPageTitle('Bulk Order');
  }, [setPageTitle]);

  // Fetch companies
  const fetchCompanies = async () => {
    try {
      if (isCompanyUser && companyId) {
        const companyDetails = apiService.getCompanyDetails();
        if (companyDetails) {
          setCompanies([companyDetails]);
        }
        return;
      }
      
      const res = await apiService.get(API_ENDPOINTS.GET_company);
      if (res?.success) {
        const companyList = res.data?.Company || [];
        setCompanies(companyList);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };
  
  useEffect(() => {
    fetchCompanies();
  }, []);

  // Add this new function for paginated data fetching with search parameter
  const fetchProductsWithPagination = async (status = statusFilter, companyOverride = null, page = 1, size = pageSize, searchQuery = null, fetchAll = false, dateOverride = null) => {
    setIsLoading(true);
    try {
      const userId = localStorage.getItem("user_id");
      const fromDate = formatDateForAPI(
        dateOverride?.startDate !== undefined ? dateOverride.startDate : startDate
      );
      const toDate = formatDateForAPI(
        dateOverride?.endDate !== undefined ? dateOverride.endDate : endDate
      );
      const statusParam = !status ? '' : `&status=${encodeURIComponent(status)}`;
      
      // *When searching, fetch all results by using a large page size
      const pageToUse = fetchAll ? 1 : page;
      const sizeToUse = fetchAll ? 10000 : size; // Use large number to get all search results
      
      // *Add pagination parameters
      let url = `${API_ENDPOINTS.GET_bulk_upload_product}?user_id=${userId}${statusParam}&page=${pageToUse}&page_size=${sizeToUse}`;
      
      // Add search parameter if present
      const activeSearch = searchQuery !== null ? searchQuery : searchTerm;
      if (activeSearch && activeSearch.trim()) {
        url += `&search=${encodeURIComponent(activeSearch.trim())}`;
      }
      
      // Add date filters only if dates are provided
      if (fromDate) {
        url += `&from_date=${fromDate}`;
      }
      if (toDate) {
        url += `&to_date=${toDate}`;
      }
      
      // Add company filter (allow override)
      if (isCompanyUser && companyId) {
        url += `&company=${companyId}`;
      } else {
        const companyToUse = companyOverride ?? selectedCompany;
        if (companyToUse && companyToUse !== 'all') {
          url += `&company=${companyToUse}`;
        }
      }
      
      const response = await apiService.get(url);
      
      if (response) {
        // Handle paginated response
        const list = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.results)
            ? response.results
            : Array.isArray(response)
              ? response
              : [];
        
        setProducts(list);
        
        // Update pagination state from response
        if (response.pagination) {
          setPagination(response.pagination);
        } else {
          // If API doesn't return pagination, create it based on the response structure
          const totalCount = response.total_count || response.total || list.length;
          const totalPages = Math.ceil(totalCount / (fetchAll ? sizeToUse : size));
          
          setPagination({
            current_page: pageToUse,
            page_size: fetchAll ? sizeToUse : size,
            total_items: totalCount,
            total_pages: totalPages,
            has_next: pageToUse < totalPages,
            has_previous: pageToUse > 1
          });
        }

        const summary = response?.summary;
        setSummaryTotals({
          total_orders: Number(summary?.total_orders) || 0,
          total_amount: summary?.total_amount ?? 0,
        });

        const companyForStatusUpdate = isCompanyUser && companyId
          ? companyId
          : (companyOverride ?? selectedCompany);
        fetchStatuses(true, companyForStatusUpdate, dateOverride, true, searchQuery);
      }
    } catch (error) {
      console.error("Error fetching products with pagination:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    setPageSize(newSize);
    setCurrentPage(1);
    // Fetch products with the new page size
    fetchProductsWithPagination(statusFilter, selectedCompany, 1, newSize);
  };

  const handlePageChange = (_, value) => {
    setCurrentPage(value);
    fetchProductsWithPagination(statusFilter, selectedCompany, value, pageSize);
  };

  // Format date for API - returns null if empty to indicate no date filter
  const formatDateForAPI = (date) => {
    if (!date || date === '') {
      return null;
    }
    if (typeof date === 'string') {
      const trimmed = date.trim();
      if (!trimmed) return null;
      return trimmed.includes('T') ? trimmed.split('T')[0] : trimmed;
    }
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
      return null;
    }
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const toDateInputValue = (date) => formatDateForAPI(date) || '';

  const formatDateChipLabel = (dateStr) => {
    if (!dateStr) return '';
    const parsed = new Date(`${dateStr}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return dateStr;
    return parsed.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatSummaryAmount = (amount) => {
    const num = Number(amount);
    if (Number.isNaN(num)) return "—";
    return `₹${num.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // Fetch dynamic statuses with counts from API
  const fetchStatuses = async (
    preserveCurrentFilter = false,
    companyOverride = null,
    dateOverride = null,
    skipProductFetch = false,
    searchOverride = null
  ) => {
    if (!skipProductFetch) {
      setIsLoading(true);
    }
    try {
      const userId = localStorage.getItem("user_id");
      const effectiveStartDate =
        dateOverride?.startDate !== undefined ? dateOverride.startDate : startDate;
      const effectiveEndDate =
        dateOverride?.endDate !== undefined ? dateOverride.endDate : endDate;
      const fromDate = formatDateForAPI(effectiveStartDate);
      const toDate = formatDateForAPI(effectiveEndDate);

      let countUrl = `${API_ENDPOINTS.GET_order_status_choices_with_count}?user_id=${userId}&type=bulk`;
      
      // Add date filters only if dates are provided
      if (fromDate) {
        countUrl += `&from_date=${fromDate}`;
      }
      if (toDate) {
        countUrl += `&to_date=${toDate}`;
      }
      
      // Add search parameter to status counts
      const activeSearch = searchOverride !== null ? searchOverride : searchTerm;
      if (activeSearch && activeSearch.trim()) {
        countUrl += `&search=${encodeURIComponent(activeSearch.trim())}`;
      }
      
      // Add company filter (allow override to avoid state race)
      if (isCompanyUser && companyId) {
        countUrl += `&company=${companyId}`;
      } else {
        const companyToUse = companyOverride ?? selectedCompany;
        if (companyToUse && companyToUse !== 'all') {
          countUrl += `&company=${companyToUse}`;
        }
      }

      // Full list keeps denied statuses visible; count API may omit them
      const [choicesResponse, countResponse] = await Promise.all([
        apiService.get(`${API_ENDPOINTS.GET_order_status_choices}?type=bulk`),
        apiService.get(countUrl),
      ]);

      const allStatuses = Array.isArray(choicesResponse?.data)
        ? choicesResponse.data
        : Array.isArray(choicesResponse?.data?.statuses)
          ? choicesResponse.data.statuses
          : [];
      const countedStatuses = Array.isArray(countResponse?.data?.statuses)
        ? countResponse.data.statuses
        : Array.isArray(countResponse?.data)
          ? countResponse.data
          : [];

      const sorted =
        allStatuses.length > 0
          ? mergeOrderStatusLists(allStatuses, countedStatuses)
          : [...countedStatuses].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));

      if (choicesResponse?.success || countResponse?.success || sorted.length > 0) {
        setStatuses(sorted);

        if (!skipProductFetch) {
          if (preserveCurrentFilter && statusFilter && !isOrderStatusTabDisabled("bulkOrders", { name: statusFilter })) {
            fetchProductsWithPagination(
              statusFilter,
              companyOverride,
              currentPage,
              pageSize,
              searchOverride,
              false,
              dateOverride
            );
          } else {
            const savedTab = consumeSavedBulkOrderStatusTab();
            const savedListState = consumeSavedBulkOrderListState();
            const restoredPage =
              Number(savedListState?.page) > 0 ? Number(savedListState.page) : 1;
            const restoredPageSize =
              Number(savedListState?.pageSize) > 0
                ? Number(savedListState.pageSize)
                : pageSize;
            const restoredSearch =
              typeof savedListState?.searchTerm === "string"
                ? savedListState.searchTerm
                : "";

            if (savedListState) {
              setCurrentPage(restoredPage);
              setPageSize(restoredPageSize);
              if (restoredSearch) {
                setSearchTerm(restoredSearch);
                setSearchValue(restoredSearch);
                setHasSearched(true);
              }
            }

            const savedTabExists =
              savedTab &&
              sorted.some((s) => s.name === savedTab) &&
              !isOrderStatusTabDisabled(
                "bulkOrders",
                sorted.find((s) => s.name === savedTab) || savedTab
              );
            const firstAllowed =
              sorted.find((s) => !isOrderStatusTabDisabled("bulkOrders", s))?.name ||
              sorted[0]?.name ||
              "";
            const initial = savedTabExists ? savedTab : firstAllowed;
            setStatusFilter(initial);
            fetchProductsWithPagination(
              initial,
              companyOverride,
              restoredPage,
              restoredPageSize,
              restoredSearch || null,
              Boolean(restoredSearch),
              dateOverride
            );
          }
        }
      } else if (!skipProductFetch) {
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error fetching statuses:", error);
      if (!skipProductFetch) {
        setIsLoading(false);
      }
    }
  };

  // Search handlers
  const handleSearch = () => {
    const trimmedSearch = searchValue.trim();
    setSearchTerm(trimmedSearch);
    setCurrentPage(1);
    setHasSearched(true);
    // Fetch products with search term - fetchAll=true to get all matching results across all pages
    fetchProductsWithPagination(statusFilter, selectedCompany, 1, pageSize, trimmedSearch, true);
  };

  const handleClearSearch = () => {
    setSearchValue("");
    setSearchTerm("");
    setCurrentPage(1);
    setHasSearched(false);
    // Clear search and fetch all data with normal pagination
    fetchProductsWithPagination(statusFilter, selectedCompany, 1, pageSize, "", false);
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  // Auto-refresh when search input is cleared
  useEffect(() => {
    if (searchValue === "" && searchTerm !== "") {
      handleClearSearch();
    }
  }, [searchValue]);

  useEffect(() => {
    const snapshot = takeBulkOrderListSnapshotIfReturning();
    if (snapshot) {
      // Returning from order details — restore exact previous UI, no API refresh.
      consumeSavedBulkOrderStatusTab();
      consumeSavedBulkOrderListState();
      setProducts(Array.isArray(snapshot.products) ? snapshot.products : []);
      if (snapshot.pagination) setPagination(snapshot.pagination);
      setCurrentPage(Number(snapshot.currentPage) > 0 ? Number(snapshot.currentPage) : 1);
      setPageSize(Number(snapshot.pageSize) > 0 ? Number(snapshot.pageSize) : 10);
      setStatusFilter(snapshot.statusFilter || '');
      setStatuses(Array.isArray(snapshot.statuses) ? snapshot.statuses : []);
      setSearchTerm(snapshot.searchTerm || '');
      setSearchValue(snapshot.searchValue || '');
      setHasSearched(Boolean(snapshot.hasSearched));
      setStartDate(snapshot.startDate || '');
      setEndDate(snapshot.endDate || '');
      if (snapshot.selectedCompany != null) setSelectedCompany(snapshot.selectedCompany);
      setShowFilters(Boolean(snapshot.showFilters));
      setIsResetClicked(Boolean(snapshot.isResetClicked));
      setIsLoading(false);
      acknowledgeBulkOrderListRestore();
      return;
    }
    // Initial load with default date range
    fetchStatuses();
  }, []);

  // Keep an in-memory snapshot so Back can restore without refetching.
  useEffect(() => {
    setBulkOrderListSnapshot({
      products,
      pagination,
      currentPage,
      pageSize,
      statusFilter,
      statuses,
      searchTerm,
      searchValue,
      hasSearched,
      startDate: toDateInputValue(startDate),
      endDate: toDateInputValue(endDate),
      selectedCompany,
      showFilters,
      isResetClicked,
    });
  }, [
    products,
    pagination,
    currentPage,
    pageSize,
    statusFilter,
    statuses,
    searchTerm,
    searchValue,
    hasSearched,
    startDate,
    endDate,
    selectedCompany,
    showFilters,
    isResetClicked,
  ]);

  // Set default dates when filter is opened and dates are empty (but not after reset)
  useEffect(() => {
    if (showFilters && (!startDate || startDate === '') && (!endDate || endDate === '') && !isResetClicked) {
      setStartDate(getFirstDayOfMonth());
      setEndDate(getToday());
    }
    // Reset the flag when filter is closed
    if (!showFilters) {
      setIsResetClicked(false);
    }
  }, [showFilters]);

  // Reset selections when status filter changes
  useEffect(() => {
    setSelectedProducts([]);
    setIsAllSelected(false);
  }, [statusFilter]);

  const handleStatusChange = (event, newValue) => {
    const statusItem = statuses.find((s) => s.name === newValue) || { name: newValue };
    if (isOrderStatusTabDisabled("bulkOrders", statusItem)) return;
    setStatusFilter(newValue);
    setCurrentPage(1);
    fetchProductsWithPagination(newValue, selectedCompany, 1, pageSize);
  };

  const handleRefresh = () => {
    fetchStatuses(true);
  };

  const handleRefreshWithBlink = () => {
    setIsBlinking(true);
    try {
      handleRefresh();
    } finally {
      setTimeout(() => setIsBlinking(false), 420);
    }
  };

  const handleApplyDateFilter = () => {
    // Fetch statuses first, which will then fetch products with pagination
    fetchStatuses(true);
  };

  const handleCompanyChange = (companyValue) => {
    setSelectedCompany(companyValue);
    setCurrentPage(1);
    // Immediately fetch statuses/products for the new company selection using override to avoid race
    fetchStatuses(true, companyValue);
  };

  // Reset selections when company filter changes
  useEffect(() => {
    setSelectedProducts([]);
    setIsAllSelected(false);
  }, [selectedCompany]);

  // Bulk upload handlers
  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const userId = localStorage.getItem("user_id");
      
      await apiService.downloadFile(
        `${API_ENDPOINTS.GET_bulk_upload_template}?user_id=${userId}`,
        'bulk_order_template.xlsx'
      );
      
      showToast("Template downloaded successfully", 'success');
    } catch (error) {
      console.error("Error downloading template:", error);
      const errorMsg = "Failed to download template. Please try again.";
      setUploadError(errorMsg);
      showToast(errorMsg, 'error');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadFile(file);
      setUploadError("");
      setUploadSuccess(false);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file && file.type.includes('sheet')) {
      setUploadFile(file);
      setUploadError("");
      setUploadSuccess(false);
    } else {
      const errorMsg = "Please select a valid Excel file (.xlsx, .xls)";
      setUploadError(errorMsg);
      showToast(errorMsg, 'error');
    }
  };

  const handleBulkUpload = async () => {
    if (!uploadFile) {
      const errorMsg = "Please select a file to upload";
      setUploadError(errorMsg);
      showToast(errorMsg, 'error');
      return;
    }

    setUploading(true);
    setUploadError("");
    setUploadSuccess(false);
    setUploadSuccessMessage("");
    setUploadResults(null);

    try {
      const userId = localStorage.getItem("user_id");
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('user_id', userId);
      const content_type = 'multipart/form-data';
      
      const response = await apiService.post(
        API_ENDPOINTS.POST_bulk_upload_product,
        formData,
        content_type
      );

      // Check if response is successful
      if (response?.success === true) {
        const successMsg = response?.message || "File uploaded successfully! Orders are being processed.";
        setUploadSuccessMessage(successMsg);
        setUploadSuccess(true);
        setUploadFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        
        // Store results if any
        if (response?.results && Array.isArray(response.results)) {
          setUploadResults(response.results);
        }
        
        // Show success toast
        showToast(successMsg, 'success');
        
        // Refresh the orders list
        setTimeout(() => {
          handleRefresh();
          setBulkUploadModal(false);
          setUploadSuccessMessage("");
          setUploadResults(null);
        }, 2000);
      } else {
        // Error case - extract detailed error message
        let errorMsg = "";
        
        // Prioritize different error message sources
        if (response?.error_message) {
          errorMsg = response.error_message;
        } else if (response?.error) {
          errorMsg = response.error;
        } else if (response?.message) {
          errorMsg = response.message;
        } else {
          errorMsg = "Upload failed. Please try again.";
        }
        
        setUploadError(errorMsg);
        
        // Store detailed results for display
        if (response?.results && Array.isArray(response.results)) {
          setUploadResults(response.results);
        }
        
        // Show error toast
        showToast(errorMsg, 'error');
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      
      let errorMsg = "Upload failed. Please check your file and try again.";
      
      // Extract error from response if available
      if (error?.response) {
        errorMsg = await extractErrorMessage(error.response);
      } else if (error?.message) {
        errorMsg = error.message;
      }
      
      setUploadError(errorMsg);
      showToast(errorMsg, 'error');
    } finally {
      setUploading(false);
    }
  };

  const resetUploadModal = () => {
    setBulkUploadModal(false);
    setUploadFile(null);
    setUploadError("");
    setUploadSuccess(false);
    setUploadSuccessMessage("");
    setUploadResults(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Selection handlers
  const handleProductSelect = (productId, isSelected) => {
    setSelectedProducts(prev => {
      if (isSelected) {
        const newSelected = [...prev, productId];
        setIsAllSelected(newSelected.length === products.length);
        return newSelected;
      } else {
        const newSelected = prev.filter(id => id !== productId);
        setIsAllSelected(false);
        return newSelected;
      }
    });
  };

  const handleSelectAll = async (selectAll) => {
    if (!selectAll) {
      setSelectedProducts([]);
      setIsAllSelected(false);
      return;
    }

    try {
      const userId = localStorage.getItem("user_id");
      const fromDate = formatDateForAPI(startDate);
      const toDate = formatDateForAPI(endDate);
      const statusParam = !statusFilter ? '' : `&status=${encodeURIComponent(statusFilter)}`;

      const totalItems = Number(pagination?.total_items) || products.length || 0;
      const fetchSize = Math.max(totalItems, 1);
      let url = `${API_ENDPOINTS.GET_bulk_upload_product}?user_id=${userId}${statusParam}&page=1&page_size=${fetchSize}`;

      if (searchTerm && searchTerm.trim()) {
        url += `&search=${encodeURIComponent(searchTerm.trim())}`;
      }
      if (fromDate) url += `&from_date=${fromDate}`;
      if (toDate) url += `&to_date=${toDate}`;

      if (isCompanyUser && companyId) {
        url += `&company=${companyId}`;
      } else if (selectedCompany && selectedCompany !== "all") {
        url += `&company=${selectedCompany}`;
      }

      const response = await apiService.get(url);
      const allRows = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.results)
          ? response.results
          : [];

      const allProductIds = allRows
        .map((product) => product?.id ?? product?.order_id)
        .filter((id) => id != null && id !== "");

      setSelectedProducts(allProductIds);
      setIsAllSelected(true);
    } catch (error) {
      console.error("Error selecting all bulk products across pages:", error);
      const currentPageIds = products
        .map((product) => product?.id ?? product?.order_id)
        .filter((id) => id != null && id !== "");
      setSelectedProducts(currentPageIds);
      setIsAllSelected(true);
    }
  };

  // Export handlers
  const handleExport = (format) => {
    const selectedData = products.filter(product => 
      selectedProducts.includes(product.id)
    );

    if (selectedData.length === 0) {
      const errorMsg = 'Please select products to export';
      alert(errorMsg);
      showToast(errorMsg, 'warning');
      return;
    }

    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const filename = `bulk_products_export_${timestamp}`;

    if (format === 'excel') {
      exportToExcel(selectedData, `${filename}.xlsx`);
      showToast(`Exported ${selectedData.length} products to Excel`, 'success');
    } else if (format === 'csv') {
      exportToCSV(selectedData, `${filename}.csv`);
      showToast(`Exported ${selectedData.length} products to CSV`, 'success');
    }
  };

  // Download invoices and/or delivery challans as a ZIP
  const handleDownloadZip = async (mode) => {
    try {
      if (selectedProducts.length === 0) {
        const errorMsg = 'Please select orders first';
        alert(errorMsg);
        showToast(errorMsg, 'warning');
        return;
      }

      const userId = localStorage.getItem("user_id");
      const fromDate = formatDateForAPI(startDate);
      const toDate = formatDateForAPI(endDate);
      const statusParam = !statusFilter ? '' : `&status=${encodeURIComponent(statusFilter)}`;

      const totalItems = Number(pagination?.total_items) || products.length || 0;
      const fetchSize = Math.max(totalItems, 1);
      let url = `${API_ENDPOINTS.GET_bulk_upload_product}?user_id=${userId}${statusParam}&page=1&page_size=${fetchSize}`;

      if (searchTerm && searchTerm.trim()) {
        url += `&search=${encodeURIComponent(searchTerm.trim())}`;
      }
      if (fromDate) url += `&from_date=${fromDate}`;
      if (toDate) url += `&to_date=${toDate}`;

      if (isCompanyUser && companyId) {
        url += `&company=${companyId}`;
      } else if (selectedCompany && selectedCompany !== "all") {
        url += `&company=${selectedCompany}`;
      }

      const response = await apiService.get(url);
      const allRows = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.results)
          ? response.results
          : [];

      const selectedKeySet = new Set(selectedProducts.map((id) => String(id)));
      const selectedData = allRows.filter((product) => {
        const idKey = product?.id != null ? String(product.id) : null;
        const orderKey = product?.order_id != null ? String(product.order_id) : null;
        return (idKey && selectedKeySet.has(idKey)) || (orderKey && selectedKeySet.has(orderKey));
      });

      const orderIds = selectedData
        .map(product => product.order?.order_id || product.order_id)
        .filter(Boolean);

      if (orderIds.length === 0) {
        const errorMsg = 'Selected orders do not have valid order IDs';
        alert(errorMsg);
        showToast(errorMsg, 'error');
        return;
      }

      const query = new URLSearchParams({
        order_ids: orderIds.join(','),
        type: mode
      }).toString();

      const filename = `bulk_orders_${mode}_${new Date().toISOString().slice(0, 10)}.zip`;

      await apiService.downloadFile(`${API_ENDPOINTS.GET_generate_zip_file}?${query}`, filename);
      showToast(`ZIP file downloaded successfully`, 'success');
    } catch (error) {
      console.error('Error creating ZIP:', error);
      const errorMsg = error?.message || 'Failed to download documents ZIP. Please try again.';
      alert(errorMsg);
      showToast(errorMsg, 'error');
    }
  };

  return (
    <Box
      pt={2}
      pb={4}
      sx={{
        animation: isBlinking ? 'blink 420ms ease-in-out' : 'none',
        '@keyframes blink': {
          '0%': { opacity: 1 },
          '50%': { opacity: 0 },
          '100%': { opacity: 1 },
        },
      }}
    >
      {/* Advanced Filters */}
      <FilterContainer>
        <FilterHeader>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <FilterList color="primary" />
              <Typography variant="h6" fontWeight={600}>
                Filters & Search
              </Typography>
              {(() => {
                const startDateStr = toDateInputValue(startDate);
                const endDateStr = toDateInputValue(endDate);
                const hasDateRange = startDateStr && endDateStr;
                const hasActiveFilters = searchTerm || hasDateRange;
                
                return hasActiveFilters && (
                  <>
                    <StatusChip
                      statuscolor={theme.palette.success.main}
                      sx={{ ml: 1 }}
                    >
                      Active
                    </StatusChip>
                    {searchTerm && (
                      <StatusChip
                        statuscolor={theme.palette.info.main}
                        sx={{ ml: 0.5 }}
                      >
                        🔍 {searchTerm}
                      </StatusChip>
                    )}
                    {hasDateRange && (
                      <StatusChip
                        statuscolor={theme.palette.info.main}
                        sx={{ ml: 0.5 }}
                      >
                        📅 {formatDateChipLabel(startDateStr)} - {formatDateChipLabel(endDateStr)}
                      </StatusChip>
                    )}
                  </>
                );
              })()}

            </Box>
            <IconButton
              onClick={() => setShowFilters(!showFilters)}
              sx={{
                transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s ease',
              }}
            >
              <ExpandMore />
            </IconButton>
          </Box>
        </FilterHeader>

        <Collapse in={showFilters}>
          <FilterContent>
            <Typography variant="caption" sx={{ mb: 1, display: "block", fontWeight: 600, color: "text.secondary" }}>
              Date Range Filter
            </Typography>
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} sm={6} md={3.5}>
                <DatePickerField
                  fullWidth
                  label="From Date"
                  type="date"
                  value={startDate || ''}
                  onChange={(e) => {
                    const newDate = e.target.value;
                    setStartDate(newDate);
                    setIsResetClicked(false);
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    max: endDate || new Date().toISOString().split('T')[0],
                  }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3.5}>
                <DatePickerField
                  fullWidth
                  label="To Date"
                  type="date"
                  value={endDate || ''}
                  onChange={(e) => {
                    const newDate = e.target.value;
                    setEndDate(newDate);
                    setIsResetClicked(false);
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: startDate || getFirstDayOfMonth(),
                    max: new Date().toISOString().split('T')[0],
                  }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.5}>
                <ActionButton
                  fullWidth
                  variant="contained"
                  startIcon={<FilterList />}
                  onClick={handleApplyDateFilter}
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                    }
                  }}
                >
                  Apply
                </ActionButton>
              </Grid>
              <Grid item xs={12} sm={6} md={2.5}>
                <ActionButton
                  fullWidth
                  variant="outlined"
                  startIcon={
                    <Refresh
                      sx={{
                        animation: isLoading ? 'spin 1s linear infinite' : 'none',
                        '@keyframes spin': {
                          '0%': { transform: 'rotate(0deg)' },
                          '100%': { transform: 'rotate(360deg)' },
                        },
                      }}
                    />
                  }
                  onClick={() => {
                    setIsBlinking(true);
                    const defaultStartDate = toDateInputValue(new Date(new Date().setDate(1)));
                    const defaultEndDate = toDateInputValue(new Date());
                    const companyToUse = isCompanyUser ? companyId : 'all';
                    setSearchValue('');
                    setSearchTerm('');
                    setHasSearched(false);
                    setStartDate(defaultStartDate);
                    setEndDate(defaultEndDate);
                    setIsResetClicked(true);
                    if (!isCompanyUser) setSelectedCompany('all');
                    setCurrentPage(1);
                    fetchProductsWithPagination(
                      statusFilter,
                      companyToUse,
                      1,
                      pageSize,
                      "",
                      false,
                      { startDate: defaultStartDate, endDate: defaultEndDate }
                    );
                    
                    setTimeout(() => setIsBlinking(false), 420);
                  }}
                  sx={{
                    borderColor: theme.palette.error.main,
                    color: theme.palette.error.main,
                    '&:hover': {
                      borderColor: theme.palette.error.dark,
                      backgroundColor: alpha(theme.palette.error.main, 0.05)
                    }
                  }}
                >
                  Refresh
                </ActionButton>
              </Grid>
            </Grid>
          </FilterContent>
        </Collapse>
      </FilterContainer>

      {/* Status Tabs */}
      <TabContainer>
        {statuses.length === 0 ? (
          <Box sx={{ display: "flex", gap: 1, overflow: "hidden", py: 1.25, px: 0.5 }}>
            {Array.from({ length: 8 }).map((_, index) => (
              <Skeleton
                key={`bulk-status-tab-skeleton-${index}`}
                animation="wave"
                variant="rounded"
                width={118}
                height={38}
                sx={{ flexShrink: 0, borderRadius: 2 }}
              />
            ))}
          </Box>
        ) : (
        <StyledTabs
          value={statusFilter}
          onChange={handleStatusChange}
          variant={isMobile ? 'scrollable' : 'fullWidth'}
          scrollButtons={isMobile ? 'auto' : false}
          allowScrollButtonsMobile
        >
          {statuses.map((s) => {
            const isActiveTab = statusFilter === s.name;
            const isDisabled = isOrderStatusTabDisabled("bulkOrders", s);
            return (
              <StyledTab
                key={s.id || s.name}
                value={s.name}
                disabled={isDisabled}
                sx={isDisabled ? {
                  opacity: 0.4,
                  color: "text.disabled",
                  cursor: "not-allowed",
                  textDecoration: "line-through",
                  "&.Mui-disabled": { opacity: 0.4 },
                } : undefined}
                label={
                  <Tooltip
                    title={isDisabled ? "You don't have access to this status tab" : ""}
                    arrow
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      {isDisabled ? <LockOutlinedIcon sx={{ fontSize: 14 }} /> : <span>{getStatusIcon(s.category || s.name)}</span>}
                      <span>{s.name}</span>
                      <OrderStatusCountBadge
                        data-order-status-count
                        active={isActiveTab && !isDisabled}
                      >
                        {s.count || 0}
                      </OrderStatusCountBadge>
                    </Box>
                  </Tooltip>
                }
              />
            );
          })}
        </StyledTabs>
        )}
      </TabContainer>

      {/* Search Controls & Action Buttons */}
      <Card sx={{ p: 1.5, mb: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            alignItems: "center",
            width: "100%",
          }}
        >
          <SearchInput
            placeholder="Search by Order ID or Email"
            onChange={(e) => {
              setSearchValue(e.target.value);
              if (hasSearched && e.target.value !== searchTerm) {
                setHasSearched(false);
              }
            }}
            value={searchValue}
            onKeyPress={handleKeyPress}
            sx={{
              width: { xs: "100%", sm: 340 },
              maxWidth: { xs: "100%", sm: 340 },
              height: 40,
              flex: "0 0 auto",
              border: "1px solid",
              borderColor: "grey.300",
              borderRadius: "8px",
              px: 1.5,
              py: 0,
              backgroundColor: "background.paper",
            }}
          />

          <Button
            variant="contained"
            startIcon={<Search />}
            onClick={handleSearch}
            disabled={
              !searchValue.trim() ||
              (searchValue === searchTerm && hasSearched)
            }
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              flexShrink: 0,
              height: 40,
              minWidth: 110,
              px: 2,
              boxShadow: "none",
              bgcolor: searchValue.trim() ? "primary.main" : "#E9EAEF",
              color: searchValue.trim() ? "common.white" : "#8B98AD",
              "&:hover": {
                bgcolor: searchValue.trim() ? "primary.dark" : "#DEE1E8",
                boxShadow: "none",
              },
              "&.Mui-disabled": {
                bgcolor: "#E9EAEF",
                color: "#8B98AD",
                opacity: 1,
              },
            }}
          >
            Search
          </Button>

          {!isCompanyUser && (
            <FormControl size="small" sx={{ minWidth: 200, height: 40 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={selectedCompany}
                label="Category"
                onChange={(e) => handleCompanyChange(e.target.value)}
                renderValue={(value) => {
                  if (value === "all") {
                    return (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Chip label="All Companies" size="small" sx={{ height: 22 }} />
                        All Category
                      </Box>
                    );
                  }
                  const company = companies.find(
                    (c) => String(c.id) === String(value)
                  );
                  return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip
                        label={company?.name || value}
                        size="small"
                        color="secondary"
                        variant="outlined"
                        sx={{ height: 22 }}
                      />
                      {company?.name || value}
                    </Box>
                  );
                }}
              >
                <MenuItem value="all">
                  <Chip label="All Companies" size="small" sx={{ mr: 1 }} />
                  All Category
                </MenuItem>
                {companies.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    <Chip
                      label={company.name}
                      size="small"
                      color="secondary"
                      variant="outlined"
                      sx={{ mr: 1 }}
                    />
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Box
            sx={{
              display: "flex",
              flexWrap: "nowrap",
              gap: 1.5,
              alignItems: "center",
              ml: { xs: 0, md: "auto" },
            }}
          >
            <Button
              variant="outlined"
              startIcon={<GetApp />}
              onClick={() => setAdvancedExportModal(true)}
              disabled={!shouldEnableButton("advanced_export")}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                flexShrink: 0,
                height: 40,
                opacity: !shouldEnableButton("advanced_export") ? 0.6 : 1,
              }}
            >
              Advanced Export
            </Button>

            {["placed", "confirmed"].includes(
              (statusFilter || "").toLowerCase()
            ) && (
              <Button
                variant="contained"
                startIcon={<CloudUpload />}
                onClick={() => setBulkUploadModal(true)}
                disabled={!shouldEnableButton("bulk_upload")}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 600,
                  flexShrink: 0,
                  height: 40,
                  opacity: !shouldEnableButton("bulk_upload") ? 0.6 : 1,
                }}
              >
                Bulk Upload
              </Button>
            )}

            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Rows</InputLabel>
              <Select
                value={pageSize}
                label="Rows"
                onChange={handlePageSizeChange}
              >
                {pageSizeOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Right: Total Amount */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              px: 1.5,
              py: 0.5,
              minWidth: 120,
              borderRadius: 1.5,
              bgcolor: alpha(theme.palette.success.main, 0.08),
              border: `1px solid ${alpha(theme.palette.success.main, 0.22)}`,
              flex: "0 0 auto",
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
              sx={{ lineHeight: 1.2, fontSize: 11 }}
            >
              Total Amount
            </Typography>
            {isLoading ? (
              <Skeleton animation="wave" variant="text" width={88} height={20} />
            ) : (
              <Typography
                variant="body2"
                fontWeight={700}
                color="success.dark"
                sx={{ lineHeight: 1.3 }}
              >
                {formatSummaryAmount(summaryTotals?.total_amount)}
              </Typography>
            )}
          </Box>
        </Box>

        {selectedProducts.length > 0 && (
          <Typography
            variant="body2"
            color="primary"
            fontWeight={600}
            sx={{ mt: 1 }}
          >
            {selectedProducts.length} selected
          </Typography>
        )}
      </Card>

      {/* Orders Table */}
      <Card sx={{ marginTop: 2 }}>
        <BulkProductList
          data={products} 
          searchQuery={searchTerm} 
          statusFilter={statusFilter}
          isLoading={isLoading} 
          onRefresh={handleRefreshWithBlink}
          selectedProducts={selectedProducts}
          onProductSelect={handleProductSelect}
          onSelectAll={handleSelectAll}
          isAllSelected={isAllSelected}
          onExport={handleExport}
          onZipDownload={handleDownloadZip}
          currentPage={pagination?.current_page}
          pageSize={pagination?.page_size}
          shouldEnableButton={shouldEnableButton}
          listFilters={{
            startDate,
            endDate,
            selectedCompany,
            showFilters,
          }}
        />
      </Card>

      {/* Showing range and pagination below the table */}
      {pagination && !isLoading && (
        <AppPaginationBar
          pagination={pagination}
          pageSize={pagination?.page_size}
          onPageChange={handlePageChange}
          itemLabel="orders"
          hidePagination={!!searchTerm}
        />
      )}

      {/* Advanced Export Modal */}
      <AdvancedExportModal
        open={advancedExportModal}
        onClose={() => setAdvancedExportModal(false)}
        onRefresh={handleRefreshWithBlink}
      />

      {/* Bulk Upload Modal */}
      <Dialog
        open={bulkUploadModal}
        onClose={resetUploadModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CloudUpload color="primary" />
            <Typography variant="h6">Bulk Upload Orders</Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Step 1: Format Check */}
            <Box sx={{ mb: 3, p: 2, backgroundColor: alpha(theme.palette.warning.main, 0.1), borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                Step 1: Check Your Format
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                If you already have data in the correct format with all required columns, you can skip to Step 3. 
                Otherwise, download template to ensure proper formatting.
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                Required columns: Name, Category, Source, Contact Number, Address, Village, Post, Taluka, District, State, Pincode, Product, Quantity, UoM, Rate, SKU
              </Typography>
            </Box>

            {/* Download Template Section */}
            <Box sx={{ mb: 3, p: 2, backgroundColor: alpha(theme.palette.info.main, 0.1), borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                Step 2: Download Template (Optional)
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Download the Excel template to ensure your data is in the correct format.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={handleDownloadTemplate}
                disabled={downloadingTemplate}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                {downloadingTemplate ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    Downloading...
                  </>
                ) : (
                  'Download Template'
                )}
              </Button>
            </Box>

            {/* Upload Section */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                Step 3: Upload Your File
              </Typography>
              
              <UploadArea
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                sx={{ mb: 2 }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                
                <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                
                {uploadFile ? (
                  <Box>
                    <Typography variant="h6" color="primary">
                      {uploadFile.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {(uploadFile.size / 1024 / 1024).toFixed(2)} MB
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Click to select file or drag & drop
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Supports .xlsx and .xls files
                    </Typography>
                  </Box>
                )}
              </UploadArea>
            </Box>


            {/* Error Message - Removed the main error message, keeping only failed items details */}
          {uploadResults && uploadResults.length > 0 && uploadResults.some(r => r.Status === "Failed" || r.Error) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" display="block" fontWeight={600}>
                  Failed Items:
                </Typography>
                {uploadResults.filter(r => r.Status === "Failed" || r.Error).map((result, idx) => (
                  <Typography key={idx} variant="caption" display="block" sx={{ fontSize: '0.7rem' }}>
                    Row {result["Sr No"] || idx + 1}: {result.Error || result.error || "Failed"}
                  </Typography>
                ))}
              </Box>
            </Alert>
          )}

            {/* Success Message */}
            {uploadSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {uploadSuccessMessage || "File uploaded successfully! Orders are being processed."}
                {uploadResults && uploadResults.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" display="block">
                      Successfully processed: {uploadResults.filter(r => r.Status === "Success").length} items
                    </Typography>
                  </Box>
                )}
              </Alert>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button 
            onClick={resetUploadModal} 
            disabled={uploading}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBulkUpload}
            variant="contained"
            disabled={!uploadFile || uploading}
            startIcon={uploading ? <CircularProgress size={16} /> : <CloudUpload />}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast Notifications */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MuiAlert
          onClose={handleToastClose}
          severity={toast.severity}
          sx={{ width: '100%' }}
          elevation={6}
          variant="filled"
        >
          {toast.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

export default BulkProducts;